import { addDays } from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

/** IANA zone for Irish timetables (handles EU DST). */
export const DUBLIN_TZ = 'Europe/Dublin' as const

/** yyyy-MM-dd in Ireland for an instant (for calendar iteration and exceptions). */
export function formatYmdInDublin(date: Date): string {
  return formatInTimeZone(date, DUBLIN_TZ, 'yyyy-MM-dd')
}

/**
 * JS getDay() convention: 0 = Sunday … 6 = Saturday, for a Dublin calendar date.
 */
export function dublinYmdWeekday(ymd: string): number {
  const noon = fromZonedTime(`${ymd}T12:00:00`, DUBLIN_TZ)
  const i = Number(formatInTimeZone(noon, DUBLIN_TZ, 'i'))
  return i === 7 ? 0 : i
}

/** Instant for timetable wall clock on a Dublin calendar day. */
export function dublinWallClockOnYmd(ymd: string, hours: number, minutes: number): Date {
  const pad = (n: number) => String(n).padStart(2, '0')
  const [y, mo, d] = ymd.split('-').map(Number)
  return fromZonedTime(`${y}-${pad(mo)}-${pad(d)}T${pad(hours)}:${pad(minutes)}:00`, DUBLIN_TZ)
}

/**
 * Start-of-day instant in Dublin for schedule activeFrom / activeUntil comparisons
 * (same intent as iterating midnight in the old server-local loop).
 */
export function dublinMidnightOnYmd(ymd: string): Date {
  return fromZonedTime(`${ymd}T00:00:00`, DUBLIN_TZ)
}

/** Every Dublin calendar day from start–end (inclusive), as yyyy-MM-dd. */
export function* eachDublinYmdInclusive(start: Date, end: Date): Generator<string> {
  let ymd = formatYmdInDublin(start)
  const endYmd = formatYmdInDublin(end)
  let cursor = fromZonedTime(`${ymd}T12:00:00`, DUBLIN_TZ)
  const endCursor = fromZonedTime(`${endYmd}T12:00:00`, DUBLIN_TZ)

  while (cursor.getTime() <= endCursor.getTime()) {
    ymd = formatYmdInDublin(cursor)
    yield ymd
    cursor = addDays(cursor, 1)
  }
}
